import { withIdentity } from "flavours/glitch/identity_context";
import PropTypes from "prop-types";
import { PureComponent } from "react";
import { createPortal } from "react-dom";

import { FormattedMessage, injectIntl, defineMessages } from "react-intl";

import classNames from "classnames";
import { Link } from "react-router-dom";

import { Icon } from "flavours/glitch/components/icon";

const messages = defineMessages({
  show: { id: "column_header.show_settings", defaultMessage: "Show settings" },
  hide: { id: "column_header.hide_settings", defaultMessage: "Hide settings" },
  moveLeft: { id: "column_header.moveLeft_settings", defaultMessage: "Move column to the left" },
  moveRight: { id: "column_header.moveRight_settings", defaultMessage: "Move column to the right" },
  pin: { id: "column_header.pin", defaultMessage: "Pin" },
  unpin: { id: "column_header.unpin", defaultMessage: "Unpin" },
  settings: { id: "column_header.settings", defaultMessage: "Column settings" },
});

class ColumnHeader extends PureComponent {

  static contextTypes = {
    router: PropTypes.object,
  };

  static propTypes = {
    intl: PropTypes.object.isRequired,
    title: PropTypes.node,
    icon: PropTypes.string,
    active: PropTypes.bool,
    multiColumn: PropTypes.bool,
    extraButton: PropTypes.node,
    showBackButton: PropTypes.bool,
    children: PropTypes.node,
    pinned: PropTypes.bool,
    placeholder: PropTypes.bool,
    onPin: PropTypes.func,
    onMove: PropTypes.func,
    onClick: PropTypes.func,
    appendContent: PropTypes.node,
    collapseIssues: PropTypes.bool,
    // Path of the app settings section for this column. Shown as a settings button instead of a panel.
    settingsLink: PropTypes.string,
    // Shown right after the title, for example a dropdown that switches what the column shows.
    titleAddon: PropTypes.node,
  };

  state = {
    collapsed: true,
    animating: false,
  };

  handleToggleClick = (e) => {
    e.stopPropagation();
    this.setState({ collapsed: !this.state.collapsed, animating: true });
  };

  handleTitleClick = () => {
    this.props.onClick?.();
  };

  handleMoveLeft = () => {
    this.props.onMove(-1);
  };

  handleMoveRight = () => {
    this.props.onMove(1);
  };

  handleBackClick = () => {
    const { router } = this.context;

    if (router.history.location?.state?.fromMastodon) {
      router.history.goBack();
    } else {
      router.history.push("/");
    }
  };

  handleTransitionEnd = () => {
    this.setState({ animating: false });
  };

  handlePin = () => {
    if (!this.props.pinned) {
      this.context.router.history.replace("/");
    }

    this.props.onPin();
  };

  render () {
    const { router } = this.context;
    const { title, icon, active, children, pinned, multiColumn, extraButton, showBackButton, intl: { formatMessage }, placeholder, appendContent, collapseIssues, settingsLink, titleAddon } = this.props;
    const { collapsed, animating } = this.state;

    const wrapperClassName = classNames("column-header__wrapper", {
      "active": active,
    });

    const buttonClassName = classNames("column-header", {
      "active": active,
    });

    const collapsibleClassName = classNames("column-header__collapsible", {
      "collapsed": collapsed,
      "animating": animating,
    });

    const collapsibleButtonClassName = classNames("column-header__button", {
      "active": !collapsed,
    });

    let extraContent, pinButton, moveButtons, backButton, collapseButton;

    if (children) {
      extraContent = (
        <div key='extra-content' className='column-header__collapsible__extra'>
          {children}
        </div>
      );
    }

    // Pinning is a single button in the header. Pinned columns also get their move arrows there.
    if (multiColumn && pinned) {
      pinButton = (
        <button key='pin-button' className='column-header__button' onClick={this.handlePin} title={formatMessage(messages.unpin)} aria-label={formatMessage(messages.unpin)}>
          <Icon id='push-pin-slash' />
        </button>
      );

      moveButtons = [
        <button key='move-left' className='column-header__button column-header__button--move' title={formatMessage(messages.moveLeft)} aria-label={formatMessage(messages.moveLeft)} onClick={this.handleMoveLeft}><Icon id='caret-left' /></button>,
        <button key='move-right' className='column-header__button column-header__button--move' title={formatMessage(messages.moveRight)} aria-label={formatMessage(messages.moveRight)} onClick={this.handleMoveRight}><Icon id='caret-right' /></button>,
      ];
    } else if (multiColumn && this.props.onPin) {
      pinButton = (
        <button key='pin-button' className='column-header__button' onClick={this.handlePin} title={formatMessage(messages.pin)} aria-label={formatMessage(messages.pin)}>
          <Icon id='push-pin' />
        </button>
      );
    }

    if (!pinned && ((multiColumn && router.history.location?.state?.fromMastodon) || showBackButton)) {
      backButton = (
        <button onClick={this.handleBackClick} className='column-header__back-button' aria-label={formatMessage({ id: "column_back_button.label", defaultMessage: "Back" })}>
          <Icon id='arrow-left' className='column-back-button__icon' />
          <span className='column-header__back-label'><FormattedMessage id='column_back_button.label' defaultMessage='Back' /></span>
        </button>
      );
    }
    
    const collapsedContent = [
      extraContent,
    ];

    let settingsButton;
    if (this.props.identity.signedIn && settingsLink) {
      settingsButton = (
        <Link className='column-header__button' to={settingsLink} title={formatMessage(messages.settings)} aria-label={formatMessage(messages.settings)}>
          <Icon id='slider-horizontal' />
        </Link>
      );
    }

    if (this.props.identity.signedIn && children) {
      collapseButton = (
        <button
          className={collapsibleButtonClassName}
          title={formatMessage(collapsed ? messages.show : messages.hide)}
          aria-label={formatMessage(collapsed ? messages.show : messages.hide)}
          onClick={this.handleToggleClick}
        >
          <div className='gts-icon-with-badge'>
            <Icon id='slider-horizontal' />
            {collapseIssues && <i className='gts-icon-with-badge__issue-badge' />}
          </div>
        </button>
      );
    }

    const hasTitle = icon && title;

    const component = (
      <div className={wrapperClassName}>
        <h1 className={buttonClassName}>
          {hasTitle && !titleAddon && (
            <button onClick={this.handleTitleClick}>
              <Icon id={icon} className='column-header__icon' />
              <span className='column-header__title-text'>{title}</span>
            </button>
          )}

          {hasTitle && titleAddon && (
            <div className='column-header__title-group'>
              <button onClick={this.handleTitleClick}>
                <Icon id={icon} className='column-header__icon' />
                <span className='column-header__title-text'>{title}</span>
              </button>
              {titleAddon}
            </div>
          )}

          {!hasTitle && backButton}

          <div className='column-header__buttons'>
            {hasTitle && backButton}
            {extraButton}
            {moveButtons}
            {pinButton}
            {settingsButton}
            {collapseButton}
          </div>
        </h1>

        <div className={collapsibleClassName} tabIndex={collapsed ? -1 : null} onTransitionEnd={this.handleTransitionEnd}>
          <div className='column-header__collapsible-inner'>
            {(!collapsed || animating) && collapsedContent}
          </div>
        </div>

        {appendContent}
      </div>
    );

    if (multiColumn || placeholder) {
      return component;
    } else {
      // The portal container and the component may be rendered to the DOM in
      // the same React render pass, so the container might not be available at
      // the time `render()` is called.
      const container = document.getElementById("tabs-bar__portal");
      if (container === null) {
        // The container wasn't available, force a re-render so that the
        // component can eventually be inserted in the container and not scroll
        // with the rest of the area.
        this.forceUpdate();
        return component;
      } else {
        return createPortal(component, container);
      }
    }
  }

}

export default withIdentity(injectIntl(ColumnHeader));
